import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { ApiError } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { User } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

/**
 * 输入验证邮件里的 6 位验证码（D91）。提示条和「账号与安全」共用。
 *
 * 链接只能在收到邮件的那台设备上点开，验证码在哪都能输。
 */
export function VerifyEmailCodeForm({ compact = false }: { compact?: boolean }) {
  const t = useT()
  const id = useId()
  const toast = useToast()
  const client = useQueryClient()
  const [code, setCode] = useState('')

  const verify = useMutation({
    mutationFn: () => authApi.verifyEmailCode(code.trim()),
    onSuccess: () => {
      client.setQueryData<User>(keys.me(), (me) => (me ? { ...me, email_verified: true } : me))
      toast.show(t('verifyCode.done'))
    },
  })
  const error = verify.error instanceof ApiError ? verify.error : null

  return (
    <form
      className={cn('flex flex-wrap items-center gap-2', compact ? '' : 'max-w-sm')}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (code.trim()) verify.mutate()
      }}
    >
      <label htmlFor={id} className={compact ? 'sr-only' : 'w-full text-sm text-ink-muted'}>
        {t('verifyCode.label')}
      </label>
      <input
        id={id}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={7}
        placeholder={compact ? t('verifyCode.placeholder') : undefined}
        aria-invalid={Boolean(error)}
        className={cn(
          'w-32 rounded-md border bg-surface px-3 py-1.5 font-mono text-[15px] tracking-widest outline-none placeholder:font-sans placeholder:tracking-normal focus:border-brand-ink',
          error ? 'border-danger' : 'border-line-strong',
        )}
      />
      <Button type="submit" size="sm" loading={verify.isPending} disabled={code.trim().length < 6}>
        {t('verifyCode.submit')}
      </Button>
      {error && (
        <p role="alert" className="w-full text-sm text-danger">
          {error.code === 'invalid_code'
            ? t('verifyCode.wrong')
            : error.status === 429
              ? t('verifyCode.locked')
              : error.message}
        </p>
      )}
    </form>
  )
}
